/**
 * SongCard 컴포넌트
 *
 * 검색 결과 곡 카드 (허밍/가사 검색 공용)
 */

import { Music, Clock, ExternalLink, Play, Pause } from 'lucide-react'
import { useI18n } from '../i18n'
import { MarqueeText } from './MarqueeText'
import type { HummingResult, LyricsResult } from '../types'

// =============================================================================
// 타입 정의
// =============================================================================

interface SongCardProps {
  /** 곡 데이터 */
  song: HummingResult | LyricsResult
  /** 순위 (1부터 시작) */
  rank: number
  /** 미리듣기 재생 중인 곡 ID */
  previewPlayingId: string | null
  /** 미리듣기 로딩 중인 곡 ID */
  previewLoading: string | null
  /** 미리듣기 토글 핸들러 */
  onTogglePreview: (song: HummingResult | LyricsResult) => void
}

// =============================================================================
// 유틸리티 함수
// =============================================================================

/**
 * 밀리초를 "분:초" 형식으로 변환
 */
const formatTime = (ms: number): string => {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

/**
 * HummingResult 타입 가드
 */
const isHummingResult = (song: HummingResult | LyricsResult): song is HummingResult => {
  return 'final_score' in song && 'matched_segment' in song
}

// =============================================================================
// 메인 컴포넌트
// =============================================================================

export function SongCard({
  song,
  rank,
  previewPlayingId,
  previewLoading,
  onTogglePreview
}: SongCardProps) {
  const { t } = useI18n()

  // 유사도 계산 (허밍: final_score, 가사: similarity)
  const similarity = isHummingResult(song) ? song.final_score : song.similarity

  /**
   * 유사도에 따른 레이블과 색상 반환
   */
  const getSimilarityLabel = (score: number): { text: string; color: string } => {
    if (score >= 0.8) return { text: t('similarityVeryHigh'), color: 'text-accent-400' }
    if (score >= 0.6) return { text: t('similarityHigh'), color: 'text-accent-500' }
    if (score >= 0.4) return { text: t('similarityMedium'), color: 'text-yellow-500' }
    return { text: t('similarityLow'), color: 'text-text-muted' }
  }

  const { text: similarityText, color: similarityColor } = getSimilarityLabel(similarity)

  return (
    <div className="bg-dark-700 rounded-xl shadow-md overflow-hidden hover:bg-dark-600 transition-colors border border-dark-500/50">
      <div className="p-4">
        {/* 상단: 순위 + 앨범아트 + 곡정보 + 유사도 */}
        <div className="flex items-start gap-3">
          {/* 순위 */}
          <div className="flex-shrink-0 w-7 h-7 bg-dark-600 rounded-full flex items-center justify-center font-bold text-text-secondary text-sm">
            {rank}
          </div>

          {/* 앨범 아트 */}
          {song.album_art_url ? (
            <img
              src={song.album_art_url}
              alt={song.album || song.title}
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-dark-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Music className="w-6 h-6 sm:w-8 sm:h-8 text-text-muted" />
            </div>
          )}

          {/* 곡 정보 */}
          <div className="flex-1 min-w-0 overflow-hidden">
            <MarqueeText
              text={song.title}
              className="font-semibold text-base sm:text-lg text-text-primary"
            />
            <MarqueeText
              text={song.artist}
              className="text-sm sm:text-base text-text-secondary"
            />
            {song.album && (
              <p className="text-xs sm:text-sm text-text-muted truncate">{song.album}</p>
            )}
          </div>

          {/* 유사도 (우측 상단) */}
          <div className="flex-shrink-0 text-right">
            <div className="text-xl sm:text-2xl font-bold text-accent-500">
              {Math.round(similarity * 100)}%
            </div>
            <div className={`text-xs sm:text-sm ${similarityColor}`}>
              {similarityText}
            </div>
          </div>
        </div>

        {/* 하단: 매칭 정보 + 버튼들 */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-dark-500/50">
          {/* 매칭 정보 (허밍: 시간, 가사: 텍스트) */}
          {isHummingResult(song) ? (
            <div className="flex items-center gap-1.5 text-xs sm:text-sm text-text-muted">
              <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="truncate">
                {formatTime(song.matched_segment.start_ms)} - {formatTime(song.matched_segment.end_ms)}
              </span>
            </div>
          ) : song.matched_lyrics ? (
            <div className="flex-1 min-w-0 mr-3">
              <MarqueeText
                text={`"${song.matched_lyrics}"`}
                className="text-xs sm:text-sm text-accent-400"
              />
            </div>
          ) : (
            <div className="flex-1" />
          )}

          {/* 버튼들 */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* 미리듣기 버튼 */}
            <button
              onClick={() => onTogglePreview(song)}
              disabled={previewLoading === song.platform_id}
              className={`p-2 rounded-full transition-colors ${
                previewLoading === song.platform_id
                  ? 'bg-gray-500 text-white cursor-wait'
                  : previewPlayingId === song.platform_id
                    ? 'bg-orange-500 text-white'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
              title={previewPlayingId === song.platform_id ? '일시정지' : '미리듣기'}
            >
              {previewLoading === song.platform_id ? (
                <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : previewPlayingId === song.platform_id ? (
                <Pause className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <Play className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>

            {song.platform_url && (
              <a
                href={song.platform_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2.5 py-1.5 sm:px-3 sm:py-1 bg-accent-500 text-white rounded-full text-xs sm:text-sm hover:bg-accent-600 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{t('listenOnDeezer')}</span>
                <span className="sm:hidden">Deezer</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* 유사도 바 */}
      <div className="h-1 bg-dark-600">
        <div
          className="h-full bg-accent-500 transition-all duration-500"
          style={{ width: `${similarity * 100}%` }}
        />
      </div>
    </div>
  )
}
